import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import {
  ContactMethod,
  allRecipientTypes,
  allRecipientTypesOptions,
} from 'components/Notifications/recipientTypes';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UPGRADE_TO_ENABLE } from './LockedUpgradeOption';

const LOCKED_RECIPIENT_TYPE: ContactMethod = 'Text-To-Speech (cell)';

type FormSelectMethodProps = {
  fieldName: string;
  rules?: { [key: string]: { value: any; message: string } };
  recipientTypeOptions?: ContactMethod[];
};

const FormSelectMethod: React.FC<FormSelectMethodProps> = ({
  fieldName,
  rules,
  recipientTypeOptions,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  register(fieldName, rules);
  const fieldValue: ContactMethod[] = watch(fieldName) || [];

  useEffect(() => {
    if (!fieldValue.includes(LOCKED_RECIPIENT_TYPE)) return;
    setValue(
      fieldName,
      fieldValue.filter((value) => value !== LOCKED_RECIPIENT_TYPE)
    );
  }, [fieldName, fieldValue, setValue]);

  const handleButtonClick = (value: ContactMethod) => {
    if (value === LOCKED_RECIPIENT_TYPE) return;
    if (fieldValue?.includes(value)) {
      setValue(
        fieldName,
        fieldValue.filter((v) => v !== value)
      );
    } else {
      setValue(
        fieldName,
        [...fieldValue, value].sort(
          (a, b) =>
            allRecipientTypesOptions.findIndex((option) => option === a) -
            allRecipientTypesOptions.findIndex((option) => option === b)
        )
      );
    }
  };

  return (
    <>
      <div className="d-flex mt-3">
        {allRecipientTypes
          .filter((option) =>
            recipientTypeOptions
              ? recipientTypeOptions.includes(option.label)
              : true
          )
          .map((option) => {
            if (option.label === LOCKED_RECIPIENT_TYPE) {
              return (
                <TooltipProvider key={option.label}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="tw:inline-flex tw:cursor-not-allowed tw:opacity-60" />
                      }
                    >
                      <StylishNewButton
                        type="button"
                        className="button--secondary me-2"
                        style={{ pointerEvents: 'none' }}
                        tabIndex={-1}
                      >
                        <SharedIcon
                          iconName={option.icon}
                          classes={'me-2'}
                          color={'light'}
                        />
                        Voice (TTS cell)
                      </StylishNewButton>
                    </TooltipTrigger>
                    <TooltipContent>{UPGRADE_TO_ENABLE}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }
            const selected = fieldValue?.includes(option.label);
            return (
              <StylishNewButton
                key={option.label}
                onClick={() => handleButtonClick(option.label)}
                className={classNames(
                  selected ? 'button--secondary-fill' : 'button--secondary',
                  'me-2'
                )}
              >
                <SharedIcon
                  iconName={selected ? 'check' : option.icon}
                  classes={'me-2'}
                  color={selected ? 'success' : 'light'}
                />
                {option.label}
              </StylishNewButton>
            );
          })}
      </div>
      {errors[fieldName] ? (
        <div className="form-text form-error">
          {errors[fieldName].message as string}
        </div>
      ) : (
        <div className="form-text-none"></div>
      )}
    </>
  );
};
export default FormSelectMethod;
